import moment from "moment";

const _ = require("lodash");

export type PenaltyType = string | null;
export type LinkedInvoicePaymentType =
  | "maintenanceFee"
  | "refurbismentFee"
  | "adminFee"
  | "terminationCharges"
  | "Overstay";

export interface LinkedInvoice {
  [key: string]: any;
  pdcId?: string;
  invoiceNumber?: string;
  description?: string;
  totalAmount?: number | string;
  chequeStatus?: string;
  status?: string;
}

export type LinkedInvoicesState = Partial<Record<LinkedInvoicePaymentType, LinkedInvoice | null>>;

export interface UploadedDocData {
  documentId?: string | null;
}

export interface MoveOutDetail {
  [key: string]: any;
  actualRentDue?: number;
  earlyTerminationPenalty?: {
    amount?: number;
    type?: string;
    vat?: number;
  };
  moveOutType?: string;
  overStayPenalty?: {
    amount?: number;
    type?: string;
    vat?: number;
  };
  overstay?: {
    [key: string]: any;
    type?: string;
  };
  termination?: {
    [key: string]: any;
    type?: string;
  };
}

interface BuildMoveOutBodyParams {
  data: any;
  type: any;
  overStayPenaltyType: PenaltyType;
  terminationPenaltyType: PenaltyType;
  moveOutDetail: MoveOutDetail | null;
  uploadedDocData: UploadedDocData | null;
  howShowFinalAmount: () => string;
  totalOverpaymentRemaining: number;
  linkedInvoices?: LinkedInvoicesState;
}

export const penaltyOption = [
  {
    label: "Early",
    value: "early",
  },
  {
    label: "Late",
    value: "late",
  },
  {
    label: "Natural",
    value: "natural",
  },
];

export const parseCurrency = (input) => {
  if (input) {
    // Preserve existing parsing behavior while keeping the conversion reusable.
    const numericValue = typeof input === "string" ? input.replace(/[^0-9.-]/g, "") : input;
    return parseFloat(numericValue || "0") || 0;
  } else {
    if (_.isEmpty(input)) {
      return null;
    } else {
      return 0;
    }
  }
};

export const linkedInvoiceFieldLabels: Record<LinkedInvoicePaymentType, string> = {
  maintenanceFee: "Maintenance Fee",
  refurbismentFee: "Refurbishment Fee",
  adminFee: "Other / Admin Fee",
  terminationCharges: "Termination Charges",
  Overstay: "OverStay",
};

const linkedInvoicePayloadPaymentTypeMap: Record<LinkedInvoicePaymentType, string> = {
  maintenanceFee: "maintenancefee",
  refurbismentFee: "refurbismentfee",
  adminFee: "otheradminfee",
  terminationCharges: "termination",
  Overstay: "overstay",
};

const linkedInvoiceSummaryPaymentTypeMap: Record<string, LinkedInvoicePaymentType> = {
  maintenancefee: "maintenanceFee",
  refurbismentfee: "refurbismentFee",
  otheradminfee: "adminFee",
  termination: "terminationCharges",
  overstay: "Overstay",
};

export const buildLinkedInvoicesPayload = (linkedInvoices?: LinkedInvoicesState) =>
  Object.entries(linkedInvoices || {})
    .filter(([, invoice]) => invoice?.pdcId)
    .map(([paymentType, invoice]) => ({
      paymentType: linkedInvoicePayloadPaymentTypeMap[paymentType as LinkedInvoicePaymentType],
      pdcId: invoice?.pdcId,
    }));

export const getLinkedInvoiceAmount = (invoice?: LinkedInvoice | null) => {
  if (!invoice) return 0;

  const totalAmount = parseCurrency(invoice.totalAmount);
  const actualAmount = parseCurrency(invoice.actualAmount);
  const vat = parseCurrency(invoice.vat);
  const chequeAmount = parseCurrency(invoice.chequeAmount);

  const resolvedTotal = totalAmount ?? actualAmount ?? chequeAmount ?? 0;
  const resolvedActual = actualAmount ?? resolvedTotal;
  const resolvedVat = vat ?? 0;

  if (resolvedActual === resolvedTotal && resolvedVat) {
    return resolvedTotal + resolvedVat;
  }

  return resolvedTotal || 0;
};

export const isLinkedInvoicePaid = (invoice?: LinkedInvoice | null) => {
  const status = String(invoice?.chequeStatus || invoice?.status || "")
    .toLowerCase()
    .trim();
  return status === "paid" || status === "fully_paid" || status === "fully paid";
};

export const getPaidLinkedInvoicesTotal = (linkedInvoices?: LinkedInvoicesState) =>
  Object.values(linkedInvoices || {}).reduce((total, invoice) => {
    if (!isLinkedInvoicePaid(invoice)) return total;
    return total + getLinkedInvoiceAmount(invoice);
  }, 0);

export const getOtherReceivablesTotal = (linkedInvoices?: LinkedInvoicesState) =>
  getPaidLinkedInvoicesTotal(linkedInvoices);

export const resolveLinkedInvoicesFromSummary = (
  paymentSchedule: LinkedInvoice[] = [],
  linkedInvoicesPayload: Array<{ paymentType?: string; pdcId?: string }> = [],
): LinkedInvoicesState =>
  linkedInvoicesPayload.reduce<LinkedInvoicesState>((acc, linkedInvoice) => {
    if (!linkedInvoice?.paymentType || !linkedInvoice?.pdcId) return acc;
    const paymentType = linkedInvoiceSummaryPaymentTypeMap[linkedInvoice.paymentType];
    if (!paymentType) return acc;

    const invoice = paymentSchedule.find((item) => item?.pdcId === linkedInvoice.pdcId) || linkedInvoice;
    if (invoice) {
      acc[paymentType] = invoice;
    }

    return acc;
  }, {});

export const buildMoveOutBody = ({
  data,
  type,
  overStayPenaltyType,
  terminationPenaltyType,
  moveOutDetail,
  uploadedDocData,
  howShowFinalAmount,
  totalOverpaymentRemaining,
  linkedInvoices,
}: BuildMoveOutBodyParams) => {
  const finalAmount = Number(parseFloat(howShowFinalAmount())?.toFixed(2));
  const maintenanceFee = linkedInvoices?.maintenanceFee
    ? getLinkedInvoiceAmount(linkedInvoices.maintenanceFee)
    : parseCurrency(data?.maintenanceFee);
  const refurbismentFee = linkedInvoices?.refurbismentFee
    ? getLinkedInvoiceAmount(linkedInvoices.refurbismentFee)
    : parseCurrency(data?.refurbismentFee);
  const otherAdminFee = linkedInvoices?.adminFee
    ? getLinkedInvoiceAmount(linkedInvoices.adminFee)
    : parseCurrency(data?.adminFee);
  const terminationCharges = linkedInvoices?.terminationCharges
    ? getLinkedInvoiceAmount(linkedInvoices.terminationCharges)
    : parseCurrency(moveOutDetail?.termination?.totalterminationPaneltyAmount);
  const overstayCharges = linkedInvoices?.Overstay
    ? getLinkedInvoiceAmount(linkedInvoices.Overstay)
    : parseCurrency(moveOutDetail?.overstay?.totalOverStayPaneltyAmount);
  const otherReceivables = getOtherReceivablesTotal(linkedInvoices);

  return {
    settlementSummary: {
      maintenancefee: maintenanceFee,
      refurbismentfee: refurbismentFee,
      otheradminfee: otherAdminFee,
      ...(linkedInvoices?.terminationCharges ? { termination: terminationCharges } : {}),
      deduction: parseCurrency(data?.waiver),
      settlementAmount: finalAmount,
      settlementAmountIsNegative: finalAmount > 0 ? "no" : "yes",
      overpaymentCredit: totalOverpaymentRemaining,
      otherReceivables,
      linkedInvoices: buildLinkedInvoicesPayload(linkedInvoices),
    },
    moveOutInspection: {
      date: data?.moveOutInspectionDate ? moment(data?.moveOutInspectionDate.$d).format("YYYY-MM-DD") : null,
      documentId: uploadedDocData?.documentId ?? null,
      moveOutConfirm: data?.moveOutConfirm,
    },
    vacatingDate: data?.moveOutDate ? moment(data?.moveOutDate.$d).format("YYYY-MM-DD") : null,
    settlementApproval: type !== 1 ? "yes" : "no",
    overStayPenalty: {
      type: overStayPenaltyType || moveOutDetail?.overStayPenalty?.type,
      amount: data?.overStayPenalty || moveOutDetail?.overStayPenalty?.amount,
      vat: moveOutDetail?.overStayPenalty?.vat,
    },
    earlyTerminationPenalty: {
      type: terminationPenaltyType || moveOutDetail?.earlyTerminationPenalty?.type,
      amount: data?.terminationPenalty || moveOutDetail?.earlyTerminationPenalty?.amount,
      vat: moveOutDetail?.earlyTerminationPenalty?.vat,
    },
    onlyBooking: true,
  };
};

export const getOverStayPenaltyValue = (moveOutDetail: MoveOutDetail | null) => {
  if (moveOutDetail?.overstay?.type?.toLowerCase() === "percentage" && moveOutDetail?.overStayPenalty?.amount && moveOutDetail?.overStayPenalty?.amount > 100) {
    return 100;
  } else {
    return moveOutDetail?.overStayPenalty?.amount;
  }
};

export const getTerminationPaneltyValue = (moveOutDetail: MoveOutDetail | null) => {
  if (moveOutDetail?.termination?.type?.toLowerCase() === "month") {
    // Existing behavior intentionally preserved for phase 1.
    return moveOutDetail?.earlyTerminationPenalty?.amount && moveOutDetail?.earlyTerminationPenalty?.amount > 12 ? 12 : moveOutDetail?.earlyTerminationPenalty?.amount;
  } else {
    return moveOutDetail?.overStayPenalty?.amount;
  }
};
